import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import { DESIGN_TOKENS } from '../../styles/constants';
import { getDashboardData, DashboardApiResponse } from '../../api/dashboardService';
import SummaryCard from '../../components/Dashboard/SummaryCard';
import PerformanceChart from '../../components/Dashboard/PerformanceChart';
import MostOrderedProducts from '../../components/Dashboard/MostOrderedProducts';

// Helper function to format currency
const formatCurrency = (amount: number, currency: string = 'QAR'): string => {
  // Format the number with 2 decimal places
  const formattedAmount = amount.toFixed(2);
  // Always use QAR prefix regardless of currency parameter
  return `QAR ${formattedAmount}`;
};

// Helper function to format change text
const formatChange = (change: number, isPercentage: boolean = false, period: string = 'This month'): string => {
  const sign = change >= 0 ? '+' : '';
  const changeText = isPercentage ? `${sign}${change}%` : `${sign}${change}`;
  return `${period} ${changeText} to last month`;
};

// Map image URL to display path - handles both full URLs and relative paths
const getImagePath = (imageUrl: string | undefined): string => {
  // Return empty string if image URL is missing or empty - ProductCard will show neutral placeholder
  if (!imageUrl || imageUrl.trim() === '') {
    return '';
  }

  // Return valid HTTP/HTTPS URLs as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }

  // For relative paths, return as is (they will be handled by the image error handler if they fail)
  return imageUrl;
};

const Dashboard: React.FC = () => {
  // API state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardApiResponse | null>(null);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await getDashboardData();
        setDashboardData(data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        
        // Handle axios errors with better messages
        if (axios.isAxiosError(err)) {
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
            setError('Unable to connect to the server. Please check your connection and try again.');
          } else if (err.response) {
            setError(`Server error: ${err.response.status} - ${err.response.statusText}`);
          } else if (err.request) {
            setError('No response from server. Please check if the API is running.');
          } else {
            setError(err.message);
          }
        } else {
          setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Chart data is now handled by PerformanceChart component

  // Transform most ordered products data
  const mostOrderedProducts = dashboardData?.mostOrdered.map((service) => ({
    id: service.productId,
    name: service.title,
    price: formatCurrency(service.price),
    description: `${service.orders} orders`,
    image: getImagePath(service.image), // Use getImagePath to handle both URLs and relative paths
    orders: service.orders,
    rank: service.rank,
  })) || [];

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: '#F6F8F8',
          minHeight: '100vh',
          padding: '40px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary }} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box
        sx={{
          backgroundColor: '#F6F8F8',
          minHeight: '100vh',
          padding: '40px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Alert
          severity="error"
          sx={{
            borderRadius: '12px',
            fontFamily: DESIGN_TOKENS.typography.fontFamily,
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Return null if no data
  if (!dashboardData) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: '#F6F8F8',
        minHeight: '100vh',
        padding: '40px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
      }}
    >
      {/* Top Row - Orders and Earnings Cards */}
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <SummaryCard
          title="Orders"
          primaryValue={dashboardData.orders.thisMonth.toString()}
          primaryChange={formatChange(dashboardData.orders.changeFromLastMonth)}
          primaryChangePositive={dashboardData.orders.changeFromLastMonth >= 0}
          secondaryValue={dashboardData.orders.totalOrders.toString()}
          secondaryLabel="Total orders"
        />
        <SummaryCard
          title="Earnings"
          primaryValue={formatCurrency(dashboardData.earnings.thisMonth, dashboardData.earnings.currency)}
          primaryChange={formatChange(dashboardData.earnings.changeFromLastMonth, true)}
          primaryChangePositive={dashboardData.earnings.changeFromLastMonth >= 0}
          secondaryValue={formatCurrency(dashboardData.earnings.totalEarned, dashboardData.earnings.currency)}
          secondaryLabel="Total earned with Eventi"
        />
      </Box>

      {/* Performance Chart Section */}
      {dashboardData?.chart && (
        <PerformanceChart 
          categories={dashboardData.chart.categories}
          orders={dashboardData.chart.orders}
          revenue={dashboardData.chart.revenue}
          feedbacks={dashboardData.chart.feedbacks}
          questions={dashboardData.chart.questions}
        />
      )}

      {/* Middle Row - Profile views and Appears in searches */}
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <SummaryCard
          title="Profile views"
          primaryValue={dashboardData.profileViews.last30Days.toString()}
          primaryChange={formatChange(dashboardData.profileViews.changeFromLastMonth, false, 'Last 30 days')}
          primaryChangePositive={dashboardData.profileViews.changeFromLastMonth >= 0}
          secondaryValue={dashboardData.profileViews.last7Days.toString()}
          secondaryLabel="Last 7 days"
          isPlanned={dashboardData.profileViews.isPlanned}
          plannedMessage={dashboardData.profileViews.message}
        />
        <SummaryCard
          title="Appears in searches"
          primaryValue={dashboardData.searchStats.appearsInSearches.toString()}
          primaryChange={formatChange(dashboardData.searchStats.changeFromLastMonth, false, 'Last 30 days')}
          primaryChangePositive={dashboardData.searchStats.changeFromLastMonth >= 0}
          secondaryValue={`${dashboardData.searchStats.clickThroughRate}%`}
          secondaryLabel="Click-through rate"
          isPlanned={dashboardData.searchStats.isPlanned}
          plannedMessage={dashboardData.searchStats.message}
        />
      </Box>

      {/* Most Ordered Products Section */}
      <MostOrderedProducts products={mostOrderedProducts} />
    </Box>
  );
};

export default Dashboard;
